
import { parseQueryBool, propsInstance } from "@/lib/util";
import { Component, MixedVue, mixins } from "@/vue";
import { VNode } from "vue";
import { Prop, Watch } from "vue-property-decorator";
import { isMobile } from "../lib/util";

const Props = propsInstance<{
  header?: string;
  body?: string;
  showHeader?: boolean;
  showFooter?: boolean;
  closeOnClickOutside?: boolean;
  modalType?: "default" | "separated-footer";
  modalSize?: "auto" | "small" | "medium" | "large";
  showBackTo?: boolean;
  showCloseModal?: boolean;
  canDragInto?: boolean;
  mobileMode?: "window" | "drawer" | "fullscreen";
  storeKey?: string;
  store?: "sessionStorage" | "localStorage";
  beforeClose?: Function;
  position?: "fixed" | "absolute";
  blurBackground?: boolean;
}>({
  header: { type: String, default: "", required: false },
  body: { type: String, default: "", required: false },
  showHeader: { type: Boolean, default: true, required: false },
  showFooter: { type: Boolean, default: true, required: false },
  closeOnClickOutside: { type: Boolean, default: true, required: false },
  modalType: { type: String as any, default: "default", required: false },
  modalSize: { type: String as any, default: "small", required: false },
  showBackTo: { type: Boolean, default: false, required: false },
  showCloseModal: { type: Boolean, default: true, required: false },
  canDragInto: { type: Boolean, default: false, required: false },
  mobileMode: { type: String as any, default: "window", required: false },
  storeKey: { type: String, default: null, required: false },
  store: { type: String as any, default: "sessionStorage", required: false },
  beforeClose: { type: Function, default: () => true, required: false },
  position: { type: String as any, default: "fixed", required: false },
  blurBackground: { type: Boolean, default: false, required: false },
});

@Component({
  name: "Modal",
})
export default class Modal extends mixins(MixedVue, Props) {
  $emit: {
    (e: "close"): any;
    (e: "drop", event: DragEvent);
  };

  $scopedSlots: {
    header: (bindings: { transitionEnded: boolean; closeModal: Modal["closeModal"] }) => VNode[];
    body: (bindings: { transitionEnded: boolean; closeModal: Modal["closeModal"] }) => VNode[];
    footer: (bindings: { transitionEnded: boolean; closeModal: Modal["closeModal"] }) => VNode[];
  };

  showModal: boolean = true;
  dragging: boolean = false;
  transitionEnded: boolean = false;
  leaveTransitionStarted: boolean = false;
  lockedScroll: [HTMLElement, number] = [null, null];

  get modalClass() {
    return ["modal-mask", this.modalSize, `mobile-${this.mobileMode}`].join(" ");
  }

  get transitionName() {
    if (!this.isMobile) return "fade";
    switch (this.mobileMode) {
      case "drawer":
        return "slide-up";
      case "fullscreen":
        return "slide-right";
      case "window":
      default:
        return "fade";
    }
  }

  get storage(): Storage {
    return window[this.store];
  }

  get isMobileFullscreen(): boolean {
    return isMobile() && this.mobileMode == "fullscreen";
  }

  @Watch("showModal")
  onShowModalChange() {
    if (this.showModal) {
      window.addEventListener("scroll", this.handleScroll, true);
    } else {
      window.removeEventListener("scroll", this.handleScroll, true);
    }
  }

  mounted() {
    if (this.storage && this.storeKey) {
      try {
        this.showModal = parseQueryBool(this.storage.getItem(this.storeKey), true);
      } catch (e) {
        this.showModal = true;
      }
    } else {
      this.showModal = true;
    }
    if (this.showModal) {
      window.addEventListener("scroll", this.handleScroll, true);
    }
  }

  beforeDestroy() {
    window.removeEventListener("scroll", this.handleScroll, true);
  }

  closeModal(): Promise<boolean> {
    return new Promise((resolve) => {
      if (!this.beforeClose()) {
        resolve(false);
        return;
      }
      this.leaveTransitionStarted = true;
      this.$on("close", () => {
        resolve(true);
        this.$off("close");
      });
    });
  }

  handleDrop(event: DragEvent) {
    if (!this.canDragInto) return;
    this.$emit("drop", event);
    this.dragging = false;
  }

  maybeHideDragging(event: DragEvent) {
    if (!this.canDragInto) return;
    const containerEl: HTMLElement = this.$el.querySelector(".modal-container");
    if (!containerEl || !event || !event.relatedTarget || !containerEl.contains(event.relatedTarget as Node)) {
      this.dragging = false;
    }
  }
  /**
   * Set flag in the storage, so the modal windows won't be
   * displayed next time. Note that this doesn't close it
   * immediately.
   */
  setDismissFlag() {
    if (this.storage && this.storeKey) {
      try {
        this.storage.setItem(this.storeKey, "false");
      } catch (e) {
        // pass
      }
    }
  }

  handleScroll(e: Event) {
    if (!this.$el.contains(e.target as Node)) {
      let target = e.target as HTMLElement;
      if (this.lockedScroll[0] == target) {
        target.scrollTop = this.lockedScroll[1];
      } else {
        this.lockedScroll[0] = target;
        this.lockedScroll[1] = target.scrollTop;
      }
    }
  }
}
